import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, Images, Wrench, Calendar } from "lucide-react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import EmergencyBanner from "@/components/EmergencyBanner";
import MobileBottomBar from "@/components/MobileBottomBar";
import Footer from "@/components/Footer";
import PageMeta from "@/components/seo/PageMeta";
import { Button } from "@/components/ui/button";
import Services from "@/components/Services";
import GoogleReviewsSection from "@/components/GoogleReviewsSection";
import RegionalServiceBanner from "@/components/RegionalServiceBanner";
import PragueServiceBanner from "@/components/PragueServiceBanner";

// Page-specific JSON-LD (only WebPage - BreadcrumbList not needed on homepage, global schemas are in index.html)
const homepageJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://eskoservis.cz/#webpage",
    "url": "https://eskoservis.cz/",
    "name": "ESKO Servis | Elektrikář Teplice - Elektroinstalace a pohotovost 24/7",
    "isPartOf": { "@id": "https://eskoservis.cz/#website" },
    "about": { "@id": "https://eskoservis.cz/#localbusiness" },
    "description": "Profesionální elektrikářské služby v Teplicích a okolí do 50 km. Elektroinstalace, revize, opravy, fotovoltaika a nonstop havarijní pohotovost 24/7.",
    "inLanguage": "cs-CZ",
    "datePublished": "2024-01-01",
    "dateModified": "2026-01-31",
    "primaryImageOfPage": {
      "@type": "ImageObject",
      "url": "https://eskoservis.cz/og-image.jpg",
      "width": 1200,
      "height": 630
    }
  }
];

const Index = () => {
  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <PageMeta
        title="Elektrikář Teplice - Elektroinstalace a pohotovost 24/7"
        description="Profesionální elektrikářské služby v Teplicích a okolí do 50 km. Elektroinstalace, revize, opravy, fotovoltaika a nonstop havarijní pohotovost 24/7. ☎ +420 732 658 712"
        canonicalPath="/"
        jsonLd={homepageJsonLd as unknown as Record<string, unknown>}
      />
      
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-lg"
      >
        Přeskočit na hlavní obsah
      </a>
      
      <Navbar />
      <Hero />
      
      {/* Main content wrapper */}
      <main id="main-content">
        {/* Emergency Banner - Prominent 24/7 Service */}
        <EmergencyBanner />
        
        {/* Services Preview */}
        <section className="py-12 md:py-16 bg-white" aria-labelledby="services-heading">
          <div className="container mx-auto px-4">
            <header className="text-center mb-8 md:mb-10">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-semibold text-sm mb-3">
                NAŠE SLUŽBY
              </span>
              <h2 id="services-heading" className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 text-foreground">
                Co pro vás <span className="text-primary">zajistíme</span>
              </h2>
              <p className="text-muted-foreground text-sm md:text-base max-w-xl mx-auto">
                Kompletní elektroinstalační služby do 1000 V pro Teplice a okolí
              </p>
            </header>
            
            <Services />
            
            <footer className="text-center mt-8">
              <Link to="/sluzby">
                <Button variant="outline" size="default" className="gap-2">
                  Zobrazit všechny služby
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </Link>
            </footer>
          </div>
        </section>

        {/* Google Reviews Widget */}
        <GoogleReviewsSection />

        {/* Portfolio CTA Block */}
        <section 
          className="py-10 md:py-12 bg-gradient-to-br from-muted/30 via-background to-muted/20 border-y border-border/30" 
          aria-labelledby="portfolio-heading"
          itemScope
          itemType="https://schema.org/ItemList"
        >
          <meta itemProp="name" content="Realizace elektroinstalací ESKO Servis" />
          <meta itemProp="description" content="Prohlédněte si naše dokončené projekty - elektroinstalace, revize a fotovoltaika v Teplicích a okolí" />
          
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
              {/* Left side - Content */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Images className="h-5 w-5 text-primary" aria-hidden="true" />
                  </div>
                  <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                    Realizace
                  </span>
                </div>
                
                <h2 
                  id="portfolio-heading" 
                  className="text-xl md:text-2xl font-bold text-foreground mb-2"
                >
                  Ukázky našich <span className="text-primary">prací</span>
                </h2>
                
                <p className="text-muted-foreground text-sm max-w-md">
                  Elektroinstalace rodinných domů, revize průmyslových rozvodů, montáž fotovoltaiky a další projekty v Teplicích, Ústí nad Labem, Mostě a okolí.
                </p>
                
                {/* SEO-rich hidden content for crawlers */}
                <div className="sr-only">
                  <ul itemProp="itemListElement">
                    <li>Elektroinstalace rodinného domu - Teplice</li>
                    <li>Revize průmyslových rozvodů - Ústí nad Labem</li>
                    <li>Montáž fotovoltaiky - Most</li>
                    <li>Nouzové osvětlení a zabezpečení - Litoměřice</li>
                    <li>Rekonstrukce elektroinstalace - Chomutov</li>
                    <li>Servis elektrospotřebičů - Děčín</li>
                  </ul>
                </div>
              </div>
              
              {/* Right side - Stats + CTA */}
              <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
                {/* Mini stats */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Wrench className="h-4 w-4 text-primary" aria-hidden="true" />
                    <span>100+ projektů</span>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-primary" aria-hidden="true" />
                    <span>Od roku 2015</span>
                  </div>
                </div>
                
                {/* CTA Button */}
                <Link to="/realizace">
                  <Button variant="electric" size="lg" className="gap-2 shadow-lg">
                    Prohlédnout realizace
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Regional Service Area Banner - Primary */}
        <RegionalServiceBanner />

        {/* Prague Service Area Banner - Secondary */}
        <PragueServiceBanner />

        {/* CTA Section */}
        <section className="py-12 md:py-16 section-gradient" aria-labelledby="cta-heading">
          <div className="container mx-auto px-4 text-center">
            <h2 id="cta-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Potřebujete <span className="text-primary">elektrikáře</span>?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Zavolejte nám nebo nám napište poptávku. Ozveme se vám co nejdříve.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a 
                href="tel:+420732658712"
                itemScope
                itemType="https://schema.org/ContactPoint"
              >
                <meta itemProp="telephone" content="+420732658712" />
                <meta itemProp="contactType" content="customer service" />
                <Button variant="electric" size="xl" className="gap-2">
                  <Phone className="h-5 w-5" aria-hidden="true" />
                  +420 732 658 712
                </Button>
              </a>
              <Link to="/kontakt">
                <Button variant="outline" size="xl">
                  Napsat poptávku
                </Button>
              </Link>
            </div>

            <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                Rychlá reakce
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                Férové ceny
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                Kvalitní práce
              </li>
            </ul>
          </div>
        </section>
      </main>

      <MobileBottomBar />
      <Footer />
    </div>
  );
};

export default Index;